import apiClient from './client';
import {
  ApiResponse,
  GitLabStatus,
  GitLabProject,
  SyncResult,
  Commit,
  CommitStats,
} from '@/types';

/**
 * Get GitLab connection status
 */
export async function getGitLabStatus(): Promise<GitLabStatus> {
  const response = await apiClient.get<ApiResponse<GitLabStatus>>('/gitlab/status');
  return response.data.data!;
}

/**
 * Get GitLab projects
 */
export async function getGitLabProjects(
  page: number = 1,
  perPage: number = 20
): Promise<{
  data: GitLabProject[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
}> {
  const response = await apiClient.get<ApiResponse<GitLabProject[]>>('/gitlab/projects', {
    params: { page, perPage },
  });
  return {
    data: response.data.data || [],
    pagination: (response.data.meta?.pagination as {
      page: number;
      perPage: number;
      total: number;
      totalPages: number;
    }) || { page: 1, perPage: 20, total: 0, totalPages: 0 },
  };
}

/**
 * Trigger full sync
 */
export async function triggerFullSync(options?: {
  since?: string;
  commitLimit?: number;
}): Promise<SyncResult> {
  const response = await apiClient.post<ApiResponse<SyncResult>>('/gitlab/sync', options);
  return response.data.data!;
}

/**
 * Sync repositories only
 */
export async function syncRepositories(): Promise<{
  created: number;
  updated: number;
  failed: number;
  errors: string[];
}> {
  const response = await apiClient.post<
    ApiResponse<{
      created: number;
      updated: number;
      failed: number;
      errors: string[];
    }>
  >('/gitlab/sync/repos');
  return response.data.data!;
}

/**
 * Map GitLab users to local users
 */
export async function syncUsers(): Promise<{
  mapped: number;
  unmapped: number;
}> {
  const response = await apiClient.post<
    ApiResponse<{
      mapped: number;
      unmapped: number;
    }>
  >('/gitlab/sync/users');
  return response.data.data!;
}

/**
 * Commit query parameters
 */
export interface CommitQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  repoId?: string;
  userId?: string;
  projectId?: string;
  authorEmail?: string;
  search?: string;
  isMergeCommit?: boolean;
  startDate?: string;
  endDate?: string;
}

/**
 * Get commits with filters
 */
export async function getCommits(params?: CommitQueryParams): Promise<{
  data: Commit[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}> {
  const response = await apiClient.get<ApiResponse<Commit[]>>('/commits', { params });
  return {
    data: response.data.data || [],
    pagination: (response.data.meta?.pagination as {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    }) || { page: 1, limit: 20, total: 0, totalPages: 0 },
  };
}

/**
 * Get recent commits
 */
export async function getRecentCommits(limit: number = 20): Promise<Commit[]> {
  const response = await apiClient.get<ApiResponse<Commit[]>>('/commits/recent', {
    params: { limit },
  });
  return response.data.data || [];
}

/**
 * Get commit statistics
 */
export async function getCommitStats(params?: {
  repoId?: string;
  userId?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<CommitStats> {
  const response = await apiClient.get<ApiResponse<CommitStats>>('/commits/stats', {
    params,
  });
  return response.data.data!;
}

/**
 * Get commits by user
 */
export async function getCommitsByUser(
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<Commit[]> {
  const response = await apiClient.get<ApiResponse<Commit[]>>(`/commits/by-user/${userId}`, {
    params: { startDate, endDate },
  });
  return response.data.data || [];
}

/**
 * Get user commit activity
 */
export async function getUserActivity(
  userId: string,
  days: number = 30
): Promise<{
  totalCommits: number;
  totalAdditions: number;
  totalDeletions: number;
  activeRepos: number;
  dailyActivity: { date: string; commits: number }[];
}> {
  const response = await apiClient.get<
    ApiResponse<{
      totalCommits: number;
      totalAdditions: number;
      totalDeletions: number;
      activeRepos: number;
      dailyActivity: { date: string; commits: number }[];
    }>
  >(`/commits/user-activity/${userId}`, { params: { days } });
  return response.data.data!;
}

/**
 * Get a specific commit
 */
export async function getCommit(id: string): Promise<Commit> {
  const response = await apiClient.get<ApiResponse<Commit>>(`/commits/${id}`);
  return response.data.data!;
}

/**
 * Get commits by repository
 */
export async function getCommitsByRepo(repoId: string, limit: number = 50): Promise<Commit[]> {
  const response = await apiClient.get<ApiResponse<Commit[]>>(`/commits/by-repo/${repoId}`, {
    params: { limit },
  });
  return response.data.data || [];
}
